// MACHINERY V2 (Stroje) — Diely a materiál: toolbar, bulk bar, column header
// (M2.5).
//
// Split out of PartsTabV2.tsx for the 300-line cap. That file owns the states
// (the query and the selection moved to usePartsTabQuery.ts at the M2.5
// review); this owns the three chrome strips. Reference
// impl: design_handoff_machinery_v3/machinery-profile-parts.jsx (`PartsTab`
// toolbar + bulk bar, `PcHead`); artboard 1009.
//
// THE BULK BAR ONLY EXISTS WITH A SELECTION, which is what makes its destructive
// button safe to render in red without a permanent scary strip on the page.
//
// No Tailwind, no shadcn, no MUI, no @emotion, no sx, no dt tokens.
import React from "react";

import useTranslation from "../../../../../shared/hooks/useTranslation";
import {
  InstalledStockItemsQuery,
  InstalledStockItemsQueryParameter,
} from "../../../../inventory/models/models";
import { SortGlyph, TabSearch, headCellStyle } from "./tabAtoms";
import TabSelect, { TabSelectOption } from "./TabSelect";
import { PartCheckbox } from "./partsAtoms";
import { PC_GRID } from "./partsRowContract";

/**
 * Search + the two facet selects + Add.
 *
 * BOARD 24 (phone) STACKS WHAT ARTBOARD 1009 (desktop) LINES UP: a full-width
 * search over a 2-up row of KAT. / ZNAČKA, and the Add CTA leaves the toolbar
 * entirely for a full-width button under the list (PartsAddCta, mounted by
 * PartsTabV2). At 390px the desktop's single wrapping flex row put the two
 * 140px-min selects on separate lines and stranded a short "+ Pridať" beside
 * one of them; the board's grid is the fix, not a restyle.
 *
 * `phone` OMITTED IS THE >= 768 TOOLBAR, UNCHANGED — same row, same order,
 * same Add button.
 */
export const PartsToolbar: React.FC<{
  search: string;
  onSearchChange: (v: string) => void;
  query: InstalledStockItemsQuery;
  categories: TabSelectOption[];
  manufacturers: TabSelectOption[];
  onQueryChange: (next: InstalledStockItemsQuery) => void;
  onAdd: () => void;
  phone?: boolean;
}> = ({
  search,
  onSearchChange,
  query,
  categories,
  manufacturers,
  onQueryChange,
  onAdd,
  phone,
}) => {
  const { translate } = useTranslation("myMachinery");

  const searchField = (
    <TabSearch
      value={search}
      onChange={onSearchChange}
      placeholder={translate("myMachinery:searchByNumberOrAlternative")}
      clearLabel={translate("myMachinery:v2.tabs.clearSearch")}
      fill={phone}
    />
  );
  const categorySelect = (
    <TabSelect
      label={translate("myMachinery:category")}
      anyLabel={translate("common:all")}
      value={query.categoryId}
      options={categories}
      onChange={(categoryId) => onQueryChange({ ...query, categoryId })}
      fill={phone}
    />
  );
  const brandSelect = (
    <TabSelect
      label={translate("common:brand")}
      anyLabel={translate("common:all")}
      value={query.manufacturerId}
      options={manufacturers}
      onChange={(manufacturerId) => onQueryChange({ ...query, manufacturerId })}
      fill={phone}
    />
  );

  if (phone) {
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 8, minWidth: 0 }}>
        {searchField}
        <div
          style={{
            display: "grid",
            // minmax(0, 1fr), never a bare 1fr: an `auto` minimum would let a
            // long category name widen its column past its share and push the
            // row into a horizontal scroll.
            gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr)",
            gap: 8,
            minWidth: 0,
          }}
        >
          {categorySelect}
          {brandSelect}
        </div>
      </div>
    );
  }

  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", minWidth: 0 }}>
      {searchField}
      {categorySelect}
      {brandSelect}
      <span style={{ flex: 1 }} />
      <button type="button" className="btn btn-mod" onClick={onAdd} style={{ whiteSpace: "nowrap" }}>
        <span aria-hidden style={{ fontSize: 15, lineHeight: 1, marginRight: 2 }}>
          +
        </span>
        {translate("myMachinery:addItem")}
      </button>
    </div>
  );
};

/** Board 24's full-width "+ Pridať položka", under the list. Phone only — at
 *  >= 768 the Add button stays in the toolbar where artboard 1009 puts it. */
export const PartsAddCta: React.FC<{ onAdd: () => void }> = ({ onAdd }) => {
  const { translate } = useTranslation("myMachinery");
  return (
    <button
      type="button"
      className="btn btn-mod"
      onClick={onAdd}
      // The 44px floor also arrives from the stylesheet — since M2.16e from the
      // MODULE-WIDE `.btn` block, not the profile one, which no longer carries
      // `.btn`. It is stated here too because this control exists only below
      // 768 and its target should not need a media query to confirm. No
      // `minWidth: 0` follows.
      style={{ height: 44, minWidth: 44, width: "100%", justifyContent: "center" }}
    >
      <span aria-hidden style={{ fontSize: 15, lineHeight: 1, marginRight: 2 }}>
        +
      </span>
      {translate("myMachinery:addItem")}
    </button>
  );
};

export const PartsBulkBar: React.FC<{
  count: number;
  busy: boolean;
  onUnlink: () => void;
  onClear: () => void;
}> = ({ count, busy, onUnlink, onClear }) => {
  const { translate } = useTranslation("myMachinery");
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 10,
        padding: "7px 12px",
        borderRadius: 8,
        background: "var(--mod-accent-soft)",
        border: "1px solid var(--mod-accent-line)",
        flexWrap: "wrap",
      }}
    >
      <span style={{ fontFamily: "var(--font-mono)", fontSize: 11, color: "var(--ink-2)" }}>
        {translate("myMachinery:v2.tabs.parts.selected", { count })}
      </span>
      <span style={{ flex: 1 }} />
      <button
        type="button"
        className="btn"
        disabled={busy}
        onClick={onUnlink}
        style={{
          whiteSpace: "nowrap",
          color: "var(--red)",
          borderColor: "color-mix(in oklab, var(--red) 40%, var(--border))",
        }}
      >
        {translate("myMachinery:v2.tabs.parts.unlinkSelected", { count })}
      </button>
      <button type="button" className="btn btn-ghost" onClick={onClear}>
        {translate("myMachinery:v2.tabs.parts.clearSelection")}
      </button>
    </div>
  );
};

const sortButton = (
  label: string,
  active: boolean,
  desc: boolean | undefined,
  onClick: () => void
) => (
  <button
    type="button"
    onClick={onClick}
    style={{ ...headCellStyle, border: 0, background: "transparent", padding: 0, cursor: "pointer" }}
  >
    {label}
    <SortGlyph dir={active ? (desc ? "desc" : "asc") : undefined} />
  </button>
);

export const PartsHeadRow: React.FC<{
  query: InstalledStockItemsQuery;
  allSelected: boolean;
  someSelected: boolean;
  onToggleAll: () => void;
  onSort: (parameter: InstalledStockItemsQueryParameter) => void;
  /** Below 1280 — the head row renders across the whole 768-1279 tablet tier,
   *  so its select-all box needs the same touch target the rows' do. */
  touch?: boolean;
}> = ({ query, allSelected, someSelected, onToggleAll, onSort, touch }) => {
  const { translate } = useTranslation("myMachinery");
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: PC_GRID,
        gap: 12,
        padding: "8px 14px",
        borderBottom: "1px solid var(--border)",
        alignItems: "center",
        minWidth: 0,
      }}
    >
      <PartCheckbox
        checked={allSelected}
        indeterminate={someSelected && !allSelected}
        label={translate("myMachinery:v2.tabs.parts.selectAll")}
        onChange={onToggleAll}
        touch={touch}
      />
      <span />
      {sortButton(
        translate("myMachinery:product"),
        query.sortBy === InstalledStockItemsQueryParameter.number,
        query.sortDesc,
        () => onSort(InstalledStockItemsQueryParameter.number)
      )}
      {sortButton(
        translate("myMachinery:brandCategory"),
        query.sortBy === InstalledStockItemsQueryParameter.manufacturer,
        query.sortDesc,
        () => onSort(InstalledStockItemsQueryParameter.manufacturer)
      )}
      <span style={headCellStyle}>{translate("myMachinery:installedInStock")}</span>
      <span style={{ ...headCellStyle, justifyContent: "flex-end" }}>
        {translate("myMachinery:v2.tabs.parts.actions")}
      </span>
    </div>
  );
};
